import { z } from 'zod'

// Organization schemas

export const organizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  settings: z.object({}).passthrough().default({}),
  is_active: z.boolean(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  user_role: z.string().optional(),
})

export type Organization = z.infer<typeof organizationSchema>

// Create organization schema
export const createOrganizationSchema = z.object({
  name: z.string().min(2).max(255),
  slug: z
    .string()
    .min(2)
    .max(100)
    .regex(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers and hyphens'),
  description: z.string().max(500).optional(),
})

export type CreateOrganizationInput = z.infer<typeof createOrganizationSchema>

// Slug check/generate response types
export interface SlugCheckResponse {
  available: boolean
  suggestion?: string
}

export interface SlugGenerateResponse {
  slug: string
}

export const organizationListResponseSchema = z.object({
  organizations: z.array(organizationSchema),
  total: z.number(),
})

export type OrganizationListResponse = z.infer<typeof organizationListResponseSchema>

// Project schemas

export const projectSettingsSchema = z.object({
  icon: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
})

export type ProjectSettings = z.infer<typeof projectSettingsSchema>

export const projectSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  settings: projectSettingsSchema.default({}),
  is_active: z.boolean(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
})

export type Project = z.infer<typeof projectSchema>

export const projectListResponseSchema = z.object({
  projects: z.array(projectSchema),
  total: z.number(),
})

export type ProjectListResponse = z.infer<typeof projectListResponseSchema>

// Create project schema
export const createProjectSchema = z.object({
  name: z.string().min(2).max(255),
  description: z
    .string()
    .max(2000)
    .optional()
    .transform((val) => val || undefined),
  icon: z
    .string()
    .max(50)
    .optional()
    .transform((val) => val || undefined),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Color must be a valid hex color (e.g. #3B82F6)')
    .or(z.literal(''))
    .optional()
    .transform((val) => val || undefined),
})

export type CreateProjectInput = z.infer<typeof createProjectSchema>

// Project create response
export interface ProjectCreateResponse {
  message: string
  project: Project
}
